import { useEffect, useRef, useState, type KeyboardEvent } from "react"
import { readableQuestion, type QuestionOption } from "./questionOptions"
import { Markdown } from "../../nira2/ds/Markdown"
import "../../nira2/case/case.css"
import "../decisions.css"
import "./checkpointCard.css"
import "./documentCard.css"
import "./cards.css"

import { BatchQuestionCard, type BatchQuestion } from "./BatchQuestionCard"

export type QuestionPayload = { question: string; why?: string; details?: string; options?: QuestionOption[]; questions?: BatchQuestion[] }

/**
 * The agent asks a question the order depends on (handoff 4, the stop card's free-answer form). The
 * card keeps the text until the send lands, so a failed send loses nothing. Enter sends; Shift+Enter
 * starts a new line, as in the reply box.
 */
type CardProps = {
  payload: QuestionPayload
  /** Resolve false when the message was not sent; the text is kept and an error is shown. */
  send: (message: string) => Promise<boolean>
  disabled?: boolean
  /** Called once after a send lands (analytics). */
  onAnswered?: () => void
}

/** Several questions (`payload.questions`) become stacked cards answered together; one question is the card below. */
export function QuestionCard(props: CardProps) {
  const { payload, ...rest } = props
  if (payload.questions && payload.questions.length > 1) return <BatchQuestionCard heading={payload.question} questions={payload.questions} {...rest} />
  return <SingleQuestionCard {...props} />
}

function SingleQuestionCard({ payload, send, disabled, onAnswered }: CardProps) {
  const view = readableQuestion(payload)
  const choices = view.options
  const defaultIndex = choices.findIndex((o) => o.default)
  // null = none picked yet, -1 = "Something else" (the typed box).
  const [picked, setPicked] = useState<number | null>(defaultIndex >= 0 ? defaultIndex : null)
  const [text, setText] = useState("")
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const blocked = disabled || pending
  const box = useRef<HTMLTextAreaElement>(null)
  // Focus once, when the card appears; later re-renders leave focus alone.
  useEffect(() => { if (choices.length === 0) box.current?.focus() }, [])
  const typing = choices.length === 0 || picked === -1
  const chosen = picked !== null && picked >= 0 ? choices[picked]?.label ?? "" : ""
  const answerNow = typing ? text.trim() : chosen
  useEffect(() => { if (picked === -1) box.current?.focus() }, [picked])
  const submit = async () => {
    const answer = answerNow
    if (!answer || blocked) return
    setPending(true)
    setFailed(false)
    let sent = false
    try {
      sent = await send(answer)
    } catch {
      sent = false
    } finally {
      setPending(false)
    }
    if (sent) { setText(""); onAnswered?.() }
    else setFailed(true)
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
  }
  return (
    <section className="nsd-cp osq" aria-label="Question">
      <p className="ndc-ov">Question</p>
      <p className="ndc-ask">{view.question}</p>
      {view.why ? <p className="ndc-why">{view.why}</p> : null}
      {view.details ? (
        <Markdown className="osq-details">{view.details}</Markdown>
      ) : null}
      <div className="osq-reply">
        {choices.length > 0 ? (
          <div className="osq-opts" role="radiogroup" aria-label="Your answer options">
            {choices.map((o, i) => (
              <label key={o.label} className={`osq-opt${picked === i ? " on" : ""}`}>
                <input type="radio" name="osq-choice" checked={picked === i} disabled={blocked} onChange={() => { setPicked(i); if (failed) setFailed(false) }} />
                <span className="osq-opt-t">
                  <span className="osq-opt-l">{o.label}{o.default ? <span className="osq-rec">Recommended</span> : null}</span>
                  {o.hint ? <span className="osq-opt-h">{o.hint}</span> : null}
                </span>
              </label>
            ))}
            <label className={`osq-opt${picked === -1 ? " on" : ""}`}>
              <input type="radio" name="osq-choice" checked={picked === -1} disabled={blocked} onChange={() => setPicked(-1)} />
              <span className="osq-opt-t"><span className="osq-opt-l">Something else</span></span>
            </label>
          </div>
        ) : null}
        <textarea
          ref={box}
          className="nsd-in osq-in"
          rows={2}
          aria-label="Your answer"
          placeholder={choices.length > 0 ? "Or type your answer" : "Type your answer"}
          value={text}
          disabled={blocked}
          onChange={(e) => { setText(e.target.value); if (choices.length > 0 && e.target.value.trim()) setPicked(-1); if (failed) setFailed(false) }}
          onKeyDown={onKey}
        />
        <div className="nsd-stop-acts">
          <button type="button" className="ncf-btn primary" disabled={blocked || !answerNow} onClick={() => void submit()}>Send answer</button>
          {failed ? <span className="osq-err" role="alert">Couldn't reach the simulator. Try again.</span> : null}
        </div>
      </div>
    </section>
  )
}
